import { Box, SxProps, Theme } from '@mui/material';
import React, { ComponentType, PropsWithChildren, ReactElement, useEffect, useMemo } from 'react';
import { DefaultValues, SubmitHandler, useForm, UseFormReturn } from 'react-hook-form';
import { logSafeAxiosError } from 'src/api/axiosClient/errorHandling';
import { FormComponentProps, FormErrorRecord } from './types';

interface FormViewProps<T extends object, P extends object> {
  formId?: string;
  formData: DefaultValues<T>;
  component: ComponentType<FormComponentProps<T, P>>;
  componentProps?: P;
  errorList?: FormErrorRecord<T>[] | undefined;
  disabled?: boolean;
  /** Return the promise of an async submit to keep `formState.isSubmitting` set until it settles. */
  onSubmit(full: T, dirty: Partial<T>, form: UseFormReturn<T>): void | Promise<unknown>;
  setFormIsDirty?: (isFormDirty: boolean) => void;
  sx?: SxProps<Theme>;
}

export const FormView = <T extends object, P extends object>(
  props: PropsWithChildren<FormViewProps<T, P>>,
): ReactElement | null => {
  const {
    formId,
    formData,
    component,
    setFormIsDirty,
    componentProps = {} as P,
    errorList,
    onSubmit,
    disabled = false,
    sx,
  } = props;
  const FormComponent = component;
  const  form = useForm<T>({
    mode: 'onBlur',
    shouldFocusError: false,
    disabled,
    defaultValues: formData || ({} as DefaultValues<T>),
  });

  const onSubmitHandler: SubmitHandler<T> = useMemo(
    () => async full => {
      try {
        // Awaited, not fired and forgotten: react-hook-form holds `isSubmitting` for exactly as long as
        // this takes, which is what disables a submit button for the duration of the request.
        await onSubmit(full, getDirtyFormData(form), form);
      } catch (e) {
        // A handler that throws would never let react-hook-form clear `isSubmitting`, leaving the form
        // disabled for good.
        logSafeAxiosError(e);
      }
    },
    [form, onSubmit],
  );

  useEffect(() => {
    if (setFormIsDirty) {
      setFormIsDirty(form.formState.isDirty);
    }
  }, [form.formState.isDirty]);

  useEffect(() => {
    if (errorList) {
      errorList.forEach(error => {
        form.setError(error.field, { type: 'custom', message: error.message });
      });
    }
  }, [errorList, form]);

  return (
    <Box
      id={formId}
      component="form"
      onSubmit={form.handleSubmit(onSubmitHandler)}
      noValidate
      sx={{ ...sx, width: '100%' }}
    >
      <FormComponent disabled={disabled} form={form} formData={formData} {...componentProps} />
    </Box>
  );
};

function getDirtyFormData<T extends object>(form: UseFormReturn<T>): Partial<T> {
  const data = form.getValues();
  const dirtyFields = form.formState.dirtyFields as unknown as Partial<Record<keyof T, boolean>>;

  return (Object.keys(dirtyFields) as (keyof T)[]).reduce<Partial<T>>((acc, key) => {
    if (dirtyFields[key]) {
      acc[key] = data[key];
    }

    return acc;
  }, {});
}
